import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import Plot from 'react-plotly.js';
import type { PlotParams } from 'react-plotly.js';
import type { Config, Data, Layout } from 'plotly.js';

import { mergeConfig, mergeLayout } from './defaults';

/**
 * Typed wrapper around `react-plotly.js`. Shared defaults live in
 * `./defaults.ts` so they can be unit-tested in isolation; this component is
 * the single seam every feature uses to render a chart.
 */

export interface PlotlyChartProps {
  data: Data[];
  layout?: Partial<Layout>;
  config?: Partial<Config>;
  className?: string;
  style?: CSSProperties;
  /** Forwarded to react-plotly.js — called once the chart is drawn. */
  onInitialized?: PlotParams['onInitialized'];
  onUpdate?: PlotParams['onUpdate'];
  /** Forwarded to react-plotly.js -- called when user clicks a data point. */
  onClick?: PlotParams['onClick'];
}

export default function PlotlyChart({
  data,
  layout,
  config,
  className,
  style,
  onInitialized,
  onUpdate,
  onClick,
}: PlotlyChartProps): JSX.Element {
  const mergedLayout = useMemo<Partial<Layout>>(() => mergeLayout(layout), [layout]);
  const mergedConfig = useMemo<Partial<Config>>(() => mergeConfig(config), [config]);

  return (
    <Plot
      data={data}
      layout={mergedLayout}
      config={mergedConfig}
      className={className}
      style={{ width: '100%', height: '100%', ...style }}
      useResizeHandler
      onInitialized={onInitialized}
      onUpdate={onUpdate}
      onClick={onClick}
    />
  );
}
